import React, { Component } from 'react';

import styled from 'styled-components';

import moment from 'moment-timezone';
import xss from 'xss';

import InlineEdit from '@qality/common-components/src/molecules/inline-edit';
import { TestCycle } from '@qality/common-model';
import {
  DateFormatService,
  StorageOfUniqueServices,
} from '@qality/common-service';
import { InputUtils } from '@qality/common-utils';

import { ExecutionStatusSummaryMultiBarChart } from '../execution-status-summary/bar-chart/ExecutionStatusSummaryMultiBarChart';
import { VersionSelectOption } from '../select/VersionSelect';
import { CloseReopenCycle } from './CloseReopenCycle';
import TestCycleDetailsTabDueDate from './TestCycleDetailsTabDueDate';
import TestCycleDetailsTabVersion from './TestCycleDetailsTabVersion';
import './_test-cycle-details-tab.scss';

type Props = {
  testCycle: TestCycle;
  canEdit: boolean;
  updateTestCycleComment?: (value: string) => Promise<void>;
  handleDueDateChange?: (value: string) => Promise<void>;
  handleVersionChange?: (value: VersionSelectOption) => Promise<void>;
  showProject?: boolean;
  showName?: boolean;
  updateTestCycle?: (updatedTestCycle: TestCycle) => void;
  showCycleStatus?: boolean;
};

type State = {
  inlineEditKey: number;
};

const TestCycleName = styled.h4`
  margin-bottom: 16px;
  word-break: break-word;
`;

const CloseOpenWrapper = styled.div`
  padding-top: 16px;
  width: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const TestCycleDetailsWrapper = styled.div`
  min-width: 400px;
`;

export default class TestCycleDetails extends Component<Props, State> {
  static defaultProps = {
    showProject: true,
    showName: false,
    showCycleStatus: true,
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      inlineEditKey: 0,
    };
  }

  handleDescriptionChange = (value: string): void => {
    const valueWithEmptyLines = InputUtils.keepEmptyLines(value);
    this.setState({
      inlineEditKey: this.state.inlineEditKey + 1,
    });
    this.props.updateTestCycleComment(valueWithEmptyLines);
  };

  render(): JSX.Element {
    const testCycle = this.props.testCycle;
    return (
      <div className='test-cycle-details-tab-container'>
        <TestCycleDetailsWrapper>
          {this.props.showName && (
            <TestCycleName>{testCycle.name}</TestCycleName>
          )}
          {this.props.showProject && (
            <div className='test-cycle-details-page-project-container'>
              <div className='qality-label'>Project:</div>
              <div
                className='test-cycle-details-page-project'
                onClick={async () =>
                  window.open(
                    StorageOfUniqueServices.get('URLService').getProjectUrl(
                      testCycle.project.key
                    ),
                    '_blank'
                  )
                }
              >
                <img
                  className='test-cycle-details-page-project-icon'
                  src={testCycle.project.iconUrl}
                  alt=''
                />
                <a className='test-cycle-details-page-project-text text-wrapper'>
                  {testCycle.project.name}
                </a>
              </div>
            </div>
          )}

          <TestCycleDetailsTabVersion
            testCycle={this.props.testCycle}
            handleVersionChange={this.props.handleVersionChange}
            canEdit={this.props.canEdit}
          />
          <div className='test-cycle-details-page-comment hide-focus-wrapper'>
            <div className='qality-label'>Description</div>
            {this.props.canEdit ? (
              <InlineEdit
                key={this.state.inlineEditKey}
                placeholder='Add description'
                text={testCycle.comment}
                onConfirm={this.handleDescriptionChange}
                length={10000}
                shouldLoadToolbar={false}
              />
            ) : (
              <div
                className='text-wrapper'
                dangerouslySetInnerHTML={{
                  __html: xss(testCycle.comment),
                }}
              />
            )}
          </div>
          <div>
            <span className='qality-label'>Created:</span>
            <span className='test-cycle-date-label'>
              {testCycle.created
                ? moment(testCycle.created).format(
                    DateFormatService.longDateFormat
                  )
                : ' No date '}
            </span>
          </div>
          <TestCycleDetailsTabDueDate
            testCycle={this.props.testCycle}
            handleDueDateChange={this.props.handleDueDateChange}
            canEdit={this.props.canEdit}
          />
          <div className='test-cycle-details-page-status'>
            <ExecutionStatusSummaryMultiBarChart
              statusSummary={testCycle.status}
              title={
                testCycle.numberOfTestCases === 1
                  ? `${testCycle.numberOfTestCases} Test Case in the Test Cycle`
                  : `${testCycle.numberOfTestCases} Test Cases in the Test Cycle`
              }
              showProgressBars
            />
          </div>
        </TestCycleDetailsWrapper>
        {this.props.showCycleStatus && (
          <CloseOpenWrapper>
            <CloseReopenCycle />
          </CloseOpenWrapper>
        )}
      </div>
    );
  }
}
